// Generated by GitHub Copilot
/**
 * Feature Toggle Service - Centralized feature toggle evaluation with global override support
 *
 * This service provides a unified interface for checking feature toggle states throughout
 * the application. It integrates with the global override logic implemented in Milestone 2
 * and respects both global feature definitions and user preferences.
 */

import { EventEmitter } from "events";
import { authFetch } from "../utils/authFetch";

class FeatureToggleService extends EventEmitter {
  constructor() {
    super();
    this.cachedFeatures = new Map(); // Cache for feature states
    this.globalDefinitions = new Map(); // Cache for global definitions
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
    this.lastFetch = 0;
    this.lastGlobalFetch = 0; // Track when we last attempted to fetch global definitions
    this.user = null;
    this.isFetching = false;
    this.hasAttemptedGlobalFetch = false; // Track if we've tried to fetch global definitions
    this.userHasDeityPrivilege = false; // Track if current user has deity privileges
    this.debugMode = process.env.REACT_APP_DEBUG === "true";
  }

  /**
   * Initialize the service with user data
   * @param {Object} user - User object from session
   */
  setUser(user) {
    this.user = user;
    this.userHasDeityPrivilege = user?.privileges?.deity || false;
    this.invalidateCache();
  }

  /**
   * Check if a feature is enabled for the current user
   * Respects global overrides from Milestone 2 implementation
   * @param {string} featureName - Name of the feature to check
   * @returns {Promise<boolean>} - True if feature is enabled, false otherwise
   */
  async isFeatureEnabled(featureName) {
    if (!featureName || typeof featureName !== "string") {
      // console.warn(
      //   "Invalid feature name provided to isFeatureEnabled:",
      //   featureName
      // );
      return false;
    }

    if (!this.user) {
      // console.warn("No user context available for feature toggle check");
      return false;
    }

    try {
      // Ensure we have fresh data
      await this.refreshCacheIfNeeded();

      // Check global definition first (implements global override logic)
      const globalDefinition = this.globalDefinitions.get(featureName);
      // console.log(`[FeatureToggle Debug] Checking feature '${featureName}':`, {
      //   globalDefinition,
      //   globalDefinitionsSize: this.globalDefinitions.size,
      //   userHasDeityPrivilege: this.userHasDeityPrivilege,
      //   hasAttemptedGlobalFetch: this.hasAttemptedGlobalFetch,
      //   debugMode: this.debugMode,
      // });

      if (globalDefinition !== undefined) {
        // Global definition exists - it takes precedence
        if (!globalDefinition.enabled) {
          // Feature is globally disabled - override user preference
          // console.log(
          //   `[FeatureToggle Debug] Feature '${featureName}' is globally disabled`
          // );
          return false;
        }
        // Feature is globally enabled - check user preference
        // console.log(
        //   `[FeatureToggle Debug] Feature '${featureName}' is globally enabled, checking user preference`
        // );
      } else {
        // console.log(
        //   `[FeatureToggle Debug] No global definition for '${featureName}', checking user preference`
        // );
      }

      // No global definition or globally enabled - check user preference
      const userFeature = this.cachedFeatures.get(featureName);
      // console.log(
      //   `[FeatureToggle Debug] User feature state for '${featureName}':`,
      //   {
      //     userFeature,
      //     cachedFeaturesSize: this.cachedFeatures.size,
      //     fallbackUserFeatures: this.user?.features?.[featureName],
      //   }
      // );

      if (userFeature !== undefined) {
        // console.log(
        //   `[FeatureToggle Debug] Returning cached user feature: ${userFeature}`
        // );
        return userFeature;
      }

      // Fallback to user.features if not in cache (backward compatibility)
      if (
        this.user.features &&
        this.user.features.hasOwnProperty(featureName)
      ) {
        // console.log(
        //   `[FeatureToggle Debug] Using fallback user.features: ${this.user.features[featureName]}`
        // );
        return this.user.features[featureName];
      }

      // Feature not found - default to disabled
      // console.log(
      //   `[FeatureToggle Debug] Feature '${featureName}' not found, defaulting to false`
      // );
      return false;
    } catch (error) {
      // console.error("Error checking feature toggle:", featureName, error);
      // Fallback to user.features on error
      return this.user.features?.[featureName] || false;
    }
  }

  /**
   * Check multiple features at once
   * @param {Array<string>} featureNames - Array of feature names to check
   * @returns {Promise<Object>} - Object with feature names as keys and boolean values
   */
  async checkMultipleFeatures(featureNames) {
    if (!Array.isArray(featureNames)) {
      throw new Error("featureNames must be an array");
    }

    const results = {};
    await Promise.all(
      featureNames.map(async (featureName) => {
        results[featureName] = await this.isFeatureEnabled(featureName);
      })
    );

    return results;
  }

  /**
   * Get all enabled features for the current user
   * @returns {Promise<Array<string>>} - Array of enabled feature names
   */
  async getEnabledFeatures() {
    if (!this.user) {
      return [];
    }

    await this.refreshCacheIfNeeded();

    const enabledFeatures = [];

    // Check all global definitions
    for (const [featureName, definition] of this.globalDefinitions) {
      if (definition.enabled) {
        // Globally enabled - check user preference
        const userEnabled =
          this.cachedFeatures.get(featureName) ||
          this.user.features?.[featureName] ||
          false;
        if (userEnabled) {
          enabledFeatures.push(featureName);
        }
      }
      // Globally disabled features are never enabled
    }

    // Check user features that don't have global definitions
    if (this.user.features) {
      for (const [featureName, enabled] of Object.entries(this.user.features)) {
        if (enabled && !this.globalDefinitions.has(featureName)) {
          enabledFeatures.push(featureName);
        }
      }
    }

    return [...new Set(enabledFeatures)]; // Remove duplicates
  }

  /**
   * Refresh cache if it's expired or empty
   * @private
   */
  async refreshCacheIfNeeded() {
    const now = Date.now();
    const needsRefresh =
      this.cachedFeatures.size === 0 || now - this.lastFetch > this.cacheExpiry;

    // Only refetch global definitions if we haven't tried recently or they're stale
    // ALL users need to fetch global definitions to see global overrides, not just deity users
    const globalNeedsRefresh =
      !this.hasAttemptedGlobalFetch ||
      now - this.lastGlobalFetch > this.cacheExpiry;

    // console.log("[FeatureToggle Debug] Cache check:", {
    //   cachedFeaturesSize: this.cachedFeatures.size,
    //   globalDefinitionsSize: this.globalDefinitions.size,
    //   userHasDeityPrivilege: this.userHasDeityPrivilege,
    //   needsRefresh,
    //   globalNeedsRefresh,
    //   hasAttemptedGlobalFetch: this.hasAttemptedGlobalFetch,
    // });

    if ((needsRefresh || globalNeedsRefresh) && !this.isFetching) {
      await this.refreshCache();
    }
  }

  /**
   * Force refresh the feature cache
   * @returns {Promise<void>}
   */
  async refreshCache() {
    if (this.isFetching) {
      return; // Prevent concurrent requests
    }

    // In debug mode, skip API calls and use user.features directly
    if (this.debugMode) {
      this.hasAttemptedGlobalFetch = true;
      this.lastGlobalFetch = Date.now();
      this.lastFetch = Date.now();
      this.emit("cacheRefreshed");
      return;
    }

    this.isFetching = true;
    try {
      // All users need to fetch global definitions to see global overrides
      // Deity users can modify them, but all users need to see the current state
      await this.fetchGlobalDefinitions();

      // Fetch user's current feature states
      await this.fetchUserFeatures();

      this.lastFetch = Date.now();
      this.emit("cacheRefreshed");
    } catch (error) {
      // console.error("Error refreshing feature cache:", error);
      throw error;
    } finally {
      this.isFetching = false;
    }
  }

  /**
   * Fetch global feature definitions from the backend
   * @private
   */
  async fetchGlobalDefinitions() {
    try {
      // console.log("[FeatureToggle Debug] Fetching global definitions...");
      const response = await authFetch("/api/v1/config/feature-definitions", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      // console.log(
      //   "[FeatureToggle Debug] Global definitions response:",
      //   response.status
      // );

      this.hasAttemptedGlobalFetch = true;
      this.lastGlobalFetch = Date.now();

      if (response.ok) {
        const definitions = await response.json();
        // console.log(
        //   "[FeatureToggle Debug] Received global definitions:",
        //   definitions
        // );
        this.globalDefinitions.clear();

        if (Array.isArray(definitions)) {
          definitions.forEach((definition) => {
            this.globalDefinitions.set(definition.name, definition);
          });
          // console.log(
          //   "[FeatureToggle Debug] Stored global definitions:",
          //   Array.from(this.globalDefinitions.entries())
          // );
        }
      } else if (response.status === 403) {
        // This should no longer happen after the backend fix, but handle gracefully
        // User doesn't have access to modify global definitions, but they should still
        // be able to read them for global override evaluation
        // console.log(
        //   "[FeatureToggle Debug] Limited access to global definitions (403) - continuing with fallback"
        // );
        // Continue with empty global definitions - this will fall back to user preferences only
        this.globalDefinitions.clear();
      } else {
        // console.warn(
        //   "[FeatureToggle Debug] Failed to fetch global feature definitions:",
        //   response.status
        // );
      }
    } catch (error) {
      // console.error(
      //   "[FeatureToggle Debug] Error fetching global feature definitions:",
      //   error
      // );
      // Don't throw - we can still work with user features only
    }
  }
  /**
   * Fetch user's current feature states
   * @private
   */
  async fetchUserFeatures() {
    try {
      // console.log("[FeatureToggle Debug] Fetching user features...");
      // Generated by GitHub Copilot - Use authFetch to handle 401 errors globally
      const response = await authFetch("/api/v1/session", {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      // console.log(
      //   "[FeatureToggle Debug] User features response:",
      //   response.status
      // );

      if (response.ok) {
        const sessionData = await response.json();
        // console.log(
        //   "[FeatureToggle Debug] Received session data features:",
        //   sessionData.features
        // );
        this.cachedFeatures.clear();

        if (sessionData.features) {
          Object.entries(sessionData.features).forEach(([name, enabled]) => {
            this.cachedFeatures.set(name, enabled);
          });
          // console.log(
          //   "[FeatureToggle Debug] Stored cached features:",
          //   Array.from(this.cachedFeatures.entries())
          // );
        }

        // Update user object if it has changed
        if (sessionData.email === this.user?.email) {
          this.user = { ...this.user, features: sessionData.features };
          // console.log(
          //   "[FeatureToggle Debug] Updated user object features:",
          //   this.user.features
          // );
        }
      } else {
        // console.warn(
        //   "[FeatureToggle Debug] Failed to fetch user features:",
        //   response.status
        // );
      }
    } catch (error) {
      // console.error(
      //   "[FeatureToggle Debug] Error fetching user features:",
      //   error
      // );
      // Use cached user.features as fallback
    }
  }

  /**
   * Invalidate cache to force refresh on next check
   */
  invalidateCache() {
    this.cachedFeatures.clear();
    this.globalDefinitions.clear();
    this.lastFetch = 0;
    this.lastGlobalFetch = 0;
    this.hasAttemptedGlobalFetch = false;
    this.emit("cacheInvalidated");
  }

  /**
   * Subscribe to feature toggle changes
   * @param {Function} callback - Callback function to call when features change
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.on("cacheRefreshed", callback);
    this.on("cacheInvalidated", callback);

    // Return unsubscribe function
    return () => {
      this.off("cacheRefreshed", callback);
      this.off("cacheInvalidated", callback);
    };
  }

  /**
   * Get cache statistics for debugging
   * @returns {Object} - Cache statistics
   */
  getCacheStats() {
    return {
      cachedFeatures: this.cachedFeatures.size,
      globalDefinitions: this.globalDefinitions.size,
      lastFetch: new Date(this.lastFetch).toISOString(),
      cacheAge: Date.now() - this.lastFetch,
      isFetching: this.isFetching,
    };
  }
}

// Export singleton instance
export const featureToggleService = new FeatureToggleService();
export default featureToggleService;
